import { supabase } from './supabase';

export type SharedRecipePreview = {
  name: string;
  servings: number;
  prepMinutes: number;
  ingredientCount?: number;
  ingredients?: Array<{ name: string; quantity: number; unit: string; optional?: boolean }>;
  instructions?: string[];
  notes?: string | null;
  sourceType?: string | null;
  sourceName?: string | null;
  sourceUrl?: string | null;
};

export type RecipeSafetyReport = {
  allergyConflicts: Array<{ person: string; rule: string; matches: string[] }>;
  dietaryWarnings: Array<{ person: string; rule: string; matches: string[] }>;
  dislikes: Array<{ person: string; item: string }>;
  hazardousTerms: string[];
  blocked: boolean;
  reviewMessage: string;
};

export type RecipeShareSummary = {
  id: string;
  recipeId?: string | null;
  recipeName: string;
  expiresAt: string;
  revokedAt?: string | null;
  importCount: number;
  maxImports: number;
  viewCount: number;
  createdAt: string;
};

function ensureClient() {
  if (!supabase) throw new Error('Weekstead backend is not configured.');
  return supabase;
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const client = ensureClient();
  const { data, error } = await client.functions.invoke('recipe-share', { body });
  if (error) {
    const context = (error as any)?.context;
    if (context && typeof context.clone === 'function') {
      try {
        const payload = await context.clone().json();
        if (payload?.error) {
          const detailed = new Error(String(payload.error)) as Error & { payload?: any };
          detailed.payload = payload;
          throw detailed;
        }
      } catch (nested) {
        if (nested instanceof Error && (nested as any).payload) throw nested;
      }
    }
    throw error;
  }
  if (data?.error) {
    const detailed = new Error(String(data.error)) as Error & { payload?: any };
    detailed.payload = data;
    throw detailed;
  }
  return data as T;
}

export async function createRecipeShareRemote(recipeId: string) {
  return invoke<{ ok: true; shareId: string; token: string; expiresAt: string; maxImports: number }>({ action: 'create', recipeId });
}

export async function previewRecipeShareRemote(token: string) {
  return invoke<{ ok: true; fullAccess: boolean; recipe: SharedRecipePreview; expiresAt: string; notice: string }>({ action: 'preview', token });
}

export async function screenRecipeShareRemote(token: string, householdId?: string | null) {
  return invoke<{ ok: true; safety: RecipeSafetyReport }>({ action: 'screen', token, householdId: householdId || undefined });
}

export async function importRecipeShareRemote(token: string, householdId?: string | null) {
  return invoke<{ ok: true; recipeId: string; existing: boolean; safety: RecipeSafetyReport }>({ action: 'import', token, householdId: householdId || undefined });
}

export async function listRecipeSharesRemote() {
  return invoke<{ ok: true; shares: RecipeShareSummary[] }>({ action: 'list' });
}

export async function revokeRecipeShareRemote(shareId: string) {
  return invoke<{ ok: true }>({ action: 'revoke', shareId });
}
